/**
 * AccordionNavigation Component
 *
 * Collapsible navigation for admin users with:
 * - Category sections that expand/collapse
 * - Auto-expand active category
 * - Badge support for unread counts
 * - Icon-only mode for tablet (with tooltips)
 *
 * Epic: MVP 1.21.2 - Dashboard Design System & Notifications (SCRUM-459)
 * Component: US-1.21.2-01 - Admin Accordion Navigation
 *
 * @component
 * @param {Object} props
 * @param {Array} props.navigation - Navigation structure with categories
 * @param {string} props.activeId - Currently active navigation item ID
 * @param {Function} props.onNavigate - Navigation callback function
 * @param {boolean} props.isCollapsed - Icon-only mode (tablet)
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronRight } from 'lucide-react';
import NotificationBadge from '../common/NotificationBadge';

const AccordionNavigation = ({
  navigation,
  activeId,
  onNavigate,
  isCollapsed = false
}) => {
  // Track expanded categories
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Auto-expand category containing active item
  useEffect(() => {
    const activeCategoryIndex = navigation.findIndex(category =>
      category.items.some(item => item.id === activeId)
    );

    if (activeCategoryIndex !== -1) {
      setExpandedCategories(prev => new Set(prev).add(activeCategoryIndex));
    }
  }, [activeId, navigation]);

  const toggleCategory = (index) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleItemClick = (item) => {
    if (onNavigate) {
      onNavigate(item.id, item.path);
    }
  };

  return (
    <nav style={styles.nav}>
      {navigation.map((category, categoryIndex) => {
        const isExpanded = expandedCategories.has(categoryIndex);
        const _hasActiveItem = category.items.some(item => item.id === activeId);

        return (
          <div key={categoryIndex} style={styles.categorySection}>
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(categoryIndex)}
              style={{
                ...styles.categoryHeader,
                ...(isCollapsed && styles.categoryHeaderCollapsed),
              }}
              title={isCollapsed ? category.category : undefined}
            >
              {!isCollapsed && (
                <>
                  <span style={styles.categoryLabel}>{category.category}</span>
                  {isExpanded ? (
                    <ChevronDown size={16} style={styles.chevron} />
                  ) : (
                    <ChevronRight size={16} style={styles.chevron} />
                  )}
                </>
              )}
              {isCollapsed && (
                <div style={styles.collapsedIndicator}>
                  {isExpanded ? '−' : '+'}
                </div>
              )}
            </button>

            {/* Category Items */}
            {isExpanded && (
              <div style={styles.itemsList}>
                {category.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.id === activeId;

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      style={{
                        ...styles.navItem,
                        ...(isActive && styles.navItemActive),
                        ...(isCollapsed && styles.navItemCollapsed),
                      }}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <Icon
                        size={20}
                        style={{
                          ...styles.icon,
                          color: isActive
                            ? 'var(--color-primary)'
                            : 'var(--color-gray-500)',
                        }}
                      />
                      {!isCollapsed && (
                        <>
                          <span style={styles.label}>{item.label}</span>
                          {item.badge && item.badge > 0 && (
                            <NotificationBadge count={item.badge} small />
                          )}
                        </>
                      )}
                      {isCollapsed && item.badge && item.badge > 0 && (
                        <div style={styles.collapsedBadge}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

const styles = {
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-4)',
  },

  categorySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-2)',
  },

  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 'var(--radius-sm)',
    transition: 'background-color var(--transition-fast)',
    width: '100%',
    textAlign: 'left',
  },

  categoryHeaderCollapsed: {
    justifyContent: 'center',
    padding: 'var(--space-2)',
  },

  categoryLabel: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  chevron: {
    color: 'var(--color-gray-500)',
  },

  collapsedIndicator: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-gray-500)',
  },

  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
    paddingLeft: 'var(--space-2)',
  },

  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    height: 'var(--nav-item-height)',
    width: '100%',
    textAlign: 'left',
    position: 'relative',
  },

  navItemCollapsed: {
    justifyContent: 'center',
    padding: 'var(--space-2)',
  },

  navItemActive: {
    backgroundColor: 'var(--color-primary-light)',
    fontWeight: 'var(--font-weight-medium)',
  },

  icon: {
    flexShrink: 0,
  },

  label: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-primary)',
    flex: 1,
  },

  collapsedBadge: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    minWidth: '16px',
    height: '16px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-error)',
    color: 'var(--color-white)',
    fontSize: '10px',
    fontWeight: 'var(--font-weight-bold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
};

AccordionNavigation.propTypes = {
  navigation: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          icon: PropTypes.elementType.isRequired,
          path: PropTypes.string,
          badge: PropTypes.number,
        })
      ).isRequired,
    })
  ).isRequired,
  activeId: PropTypes.string,
  onNavigate: PropTypes.func,
  isCollapsed: PropTypes.bool,
};

export default AccordionNavigation;
